import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Shuffle, Crown } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { id: 'feminino', label: 'Feminino' },
  { id: 'open', label: 'Open' },
  { id: 'misto', label: 'Misto' },
  { id: 'rei_rainha', label: 'Rei e Rainha' },
];

const genPairs = (duplas) => {
  const out = [];
  for (let i = 0; i < duplas.length; i++) {
    for (let j = i + 1; j < duplas.length; j++) out.push([duplas[i], duplas[j], i, j]);
  }
  return out;
};

function ScoreRow({ slotId, d1, d2, initial1, initial2, category, phase, onSaved }) {
  const [s1, setS1] = useState(initial1 ?? '');
  const [s2, setS2] = useState(initial2 ?? '');
  const [saving, setSaving] = useState(false);
  const disabled = !d1 || d1 === '-' || !d2 || d2 === '-';
  const save = async () => {
    if (s1 === '' || s2 === '') return toast.error('Preencha os dois placares');
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/tk-league/tournament/match`, {
        category, phase, slot_id: slotId, dupla1: d1, dupla2: d2, score1: parseInt(s1), score2: parseInt(s2),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Placar salvo');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/tk-league/tournament/match`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { category, phase, slot_id: slotId },
      });
      setS1(''); setS2('');
      toast.success('Placar removido');
      onSaved?.();
    } catch (e) { toast.error('Erro'); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-3 py-3 border-b border-[#F0EDE5] last:border-0" data-testid={`match-row-${slotId}`}>
      <span className="flex-1 min-w-0 text-sm text-[#1A1A1A] font-medium whitespace-normal break-words leading-tight text-right" title={d1}>{d1 || '-'}</span>
      <input type="number" min="0" value={s1} onChange={e => setS1(e.target.value)} disabled={disabled} className="w-16 h-14 flex-shrink-0 border-2 border-[#E6E2D8] rounded-lg text-center text-2xl font-bold text-[#1A1A1A] focus:border-[#E85D04] focus:outline-none disabled:bg-[#F5F2EB] disabled:text-[#C8C4B8]" data-testid={`score1-${slotId}`} />
      <span className="text-base text-[#73706A] font-bold flex-shrink-0">×</span>
      <input type="number" min="0" value={s2} onChange={e => setS2(e.target.value)} disabled={disabled} className="w-16 h-14 flex-shrink-0 border-2 border-[#E6E2D8] rounded-lg text-center text-2xl font-bold text-[#1A1A1A] focus:border-[#E85D04] focus:outline-none disabled:bg-[#F5F2EB] disabled:text-[#C8C4B8]" data-testid={`score2-${slotId}`} />
      <span className="flex-1 min-w-0 text-sm text-[#1A1A1A] font-medium whitespace-normal break-words leading-tight" title={d2}>{d2 || '-'}</span>
      <div className="flex flex-col gap-1 flex-shrink-0">
        <button onClick={save} disabled={saving || disabled} className="text-xs bg-[#E85D04] text-white px-3 py-1.5 rounded hover:bg-[#DC2F02] disabled:opacity-50 font-semibold" data-testid={`save-${slotId}`}>Salvar</button>
        <button onClick={clear} disabled={saving} className="text-xs text-[#73706A] hover:text-[#9B2226] border border-[#E6E2D8] rounded px-3 py-1" title="Limpar">Limpar</button>
      </div>
    </div>
  );
}

function GroupCard({ groupId, groupData, category, onSaved }) {
  const duplas = groupData.duplas || [];
  const pairs = genPairs(duplas);
  const standings = groupData.standings || [];
  const matches = groupData.matches || [];
  const getSaved = (slotId) => matches.find(m => m.slot_id === slotId);
  return (
    <div className="bg-white border border-[#E6E2D8] rounded-xl p-4 space-y-3">
      <h4 className="font-bold text-[#1A1A1A]">Grupo {groupId}</h4>
      <div>
        <p className="text-xs uppercase font-semibold text-[#73706A] mb-1">Classificação</p>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[#73706A] border-b border-[#E6E2D8]">
              <th className="text-left py-1">#</th><th className="text-left">Dupla</th><th>J</th><th>V</th><th>D</th><th>Sal</th><th>PTS</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((r, i) => (
              <tr key={r.dupla} className="border-b border-[#F0EDE5]" data-testid={`standing-${groupId}-${i}`}>
                <td className="py-1 font-bold text-[#E85D04]">{i + 1}º</td>
                <td className="text-[#1A1A1A]">{r.dupla}</td>
                <td className="text-center">{r.j}</td><td className="text-center">{r.v}</td><td className="text-center">{r.d}</td>
                <td className="text-center font-medium">{r.saldo >= 0 ? '+' : ''}{r.saldo}</td>
                <td className="text-center font-bold text-[#E85D04]">{r.pts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <p className="text-xs uppercase font-semibold text-[#73706A] mb-1">Jogos</p>
        {pairs.map(([d1, d2, i, j]) => {
          const slot = `${groupId}:${i}-${j}`;
          const saved = getSaved(slot);
          return <ScoreRow key={slot} slotId={slot} d1={d1} d2={d2} initial1={saved?.score1} initial2={saved?.score2} category={category} phase="group" onSaved={onSaved} />;
        })}
      </div>
    </div>
  );
}

function BracketMatch({ m, category, phase, onSaved }) {
  if (!m) return null;
  return <ScoreRow slotId={m.slot_id} d1={m.dupla1} d2={m.dupla2} initial1={m.score1} initial2={m.score2} category={category} phase={phase} onSaved={onSaved} />;
}

function BracketBlock({ title, matches, category, phase, onSaved, testid }) {
  return (
    <div className="bg-white border border-[#E6E2D8] rounded-xl p-4" data-testid={testid}>
      <h4 className="font-bold text-[#1A1A1A] mb-2">{title}</h4>
      {Array.isArray(matches) ? matches.map(m => <BracketMatch key={m.slot_id} m={m} category={category} phase={phase} onSaved={onSaved} />) : <BracketMatch m={matches} category={category} phase={phase} onSaved={onSaved} />}
    </div>
  );
}

function GroupsEditor({ cat, groups, onSaved, onCancel }) {
  const [draft, setDraft] = useState(() => {
    const d = {};
    Object.entries(groups).forEach(([gid, gd]) => { d[gid] = [...(gd.duplas || gd.players || [])]; });
    return d;
  });
  const [saving, setSaving] = useState(false);
  const update = (gid, idx, val) => setDraft(p => ({ ...p, [gid]: p[gid].map((v, i) => (i === idx ? val : v)) }));
  const save = async () => {
    for (const gid of Object.keys(draft)) {
      if (draft[gid].some(v => !v.trim())) return toast.error('Preencha todas as duplas');
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/tk-league/tournament/duplas`, { category: cat, groups: draft }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Duplas atualizadas');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); } finally { setSaving(false); }
  };
  return (
    <div className="bg-white border-2 border-[#E85D04] rounded-xl p-4 mb-6 space-y-4" data-testid="duplas-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-['Outfit'] font-bold text-[#1A1A1A]">Editar Duplas</h3>
        <div className="flex gap-2">
          <button onClick={onCancel} className="text-sm text-[#73706A] px-3 py-1.5 hover:text-[#1A1A1A]" data-testid="cancel-duplas-btn">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-sm bg-[#E85D04] text-white px-4 py-1.5 rounded-lg hover:bg-[#DC2F02] disabled:opacity-50 font-semibold" data-testid="save-duplas-btn">{saving ? 'Salvando...' : 'Salvar duplas'}</button>
        </div>
      </div>
      <p className="text-xs text-[#73706A]">Renomeie ou troque as duplas de cada grupo. Os placares já lançados são mantidos e remapeados pela posição.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Object.entries(draft).map(([gid, list]) => (
          <div key={gid} className="border border-[#E6E2D8] rounded-lg p-3">
            <p className="font-bold text-sm text-[#1A1A1A] mb-2">Grupo {gid}</p>
            <div className="space-y-2">
              {list.map((name, i) => (
                <input key={i} value={name} onChange={e => update(gid, i, e.target.value)} className="w-full border border-[#E6E2D8] rounded px-2 py-2 text-sm focus:border-[#E85D04] focus:outline-none" data-testid={`dupla-input-${gid}-${i}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReiRainhaPanel({ cat, data, onSaved }) {
  const groups = data.groups || {};
  const b = data.bracket || {};
  const [shuffling, setShuffling] = useState(null);
  const sortear = async (gid) => {
    const players = [...(groups[gid].players || [])];
    for (let i = players.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [players[i], players[j]] = [players[j], players[i]]; }
    setShuffling(gid);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      Object.entries(groups).forEach(([g, gd]) => { payload[g] = g === gid ? players : [...(gd.players || [])]; });
      await axios.post(`${API}/admin/tk-league/tournament/duplas`, { category: cat, groups: payload }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Duplas sorteadas');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao sortear'); } finally { setShuffling(null); }
  };
  return (
    <div className="space-y-8" data-testid="rei-rainha-panel">
      {Object.entries(groups).map(([gid, gd]) => (
        <section key={gid} className="bg-white border border-[#E6E2D8] rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A]">Grupo Geral — {(gd.players || []).length} jogadores</h3>
            <button onClick={() => sortear(gid)} disabled={shuffling === gid} className="text-sm bg-[#0077B6] text-white px-4 py-1.5 rounded-lg hover:bg-[#023E8A] disabled:opacity-50 font-semibold flex items-center gap-1.5" data-testid={`shuffle-${gid}`}>
              <Shuffle size={16} weight="bold" /> {shuffling === gid ? 'Sorteando...' : 'Sortear duplas'}
            </button>
          </div>
          <div>
            <p className="text-xs uppercase font-semibold text-[#73706A] mb-1">Classificação (mais vitórias · desempate por saldo) · 2 primeiros vão à final</p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#73706A] border-b border-[#E6E2D8]">
                  <th className="text-left py-1">#</th><th className="text-left">Jogador</th><th>J</th><th>V</th><th>D</th><th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {(gd.standings || []).map((r, i) => (
                  <tr key={r.player} className={`border-b border-[#F0EDE5] ${i < 2 ? 'bg-orange-50' : ''}`} data-testid={`rr-standing-${gid}-${i}`}>
                    <td className="py-1 font-bold text-[#E85D04]">{i + 1}º</td>
                    <td className="text-[#1A1A1A] font-medium">{r.player}{i < 2 && <Crown size={12} weight="fill" className="inline ml-1 text-[#FFB703]" />}</td>
                    <td className="text-center">{r.j}</td><td className="text-center">{r.v}</td><td className="text-center">{r.d}</td>
                    <td className="text-center font-medium">{r.saldo >= 0 ? '+' : ''}{r.saldo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-3">
            <p className="text-xs uppercase font-semibold text-[#73706A]">Rodadas — duplas mudam a cada jogo</p>
            {(gd.rounds || []).map(rd => (
              <div key={rd.round} className="border border-[#F0EDE5] rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-[#1A1A1A]">Rodada {rd.round}</p>
                  {rd.resting && rd.resting.length > 0 && (
                    <p className="text-[11px] text-[#73706A]">Descansando: <span className="font-semibold">{rd.resting.join(' e ')}</span></p>
                  )}
                </div>
                {rd.games.map(gm => <ScoreRow key={gm.slot_id} slotId={gm.slot_id} d1={gm.team1} d2={gm.team2} initial1={gm.score1} initial2={gm.score2} category={cat} phase="rr" onSaved={onSaved} />)}
              </div>
            ))}
          </div>
        </section>
      ))}
      <section>
        <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-3">Final — 1º × 2º do grupo</h3>
        <div className="bg-white border border-[#E6E2D8] rounded-xl p-4 max-w-2xl" data-testid="rr-final-block">
          <ScoreRow slotId={b.final?.slot_id || 'final:0'} d1={b.final?.dupla1} d2={b.final?.dupla2} initial1={b.final?.score1} initial2={b.final?.score2} category={cat} phase="final" onSaved={onSaved} />
        </div>
      </section>
    </div>
  );
}

function CategoryPanel({ cat, data, onSaved }) {
  const groups = data.groups || {};
  const b = data.bracket || {};
  const t = data.config?.type;
  if (t && t.startsWith('rei_rainha')) return <ReiRainhaPanel cat={cat} data={data} onSaved={onSaved} />;
  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-3">Fase de Grupos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Object.entries(groups).map(([gid, gd]) => <GroupCard key={gid} groupId={gid} groupData={gd} category={cat} onSaved={onSaved} />)}
        </div>
      </section>
      <section>
        <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-3">Chaveamento</h3>
        {t === 'groups_2x4_semi' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BracketBlock title="Semifinais" matches={b.semi || []} category={cat} phase="semi" onSaved={onSaved} testid="semi-block" />
            <BracketBlock title="Final" matches={b.final} category={cat} phase="final" onSaved={onSaved} testid="final-block" />
            <BracketBlock title="3º Lugar" matches={b.third} category={cat} phase="third" onSaved={onSaved} testid="third-block" />
          </div>
        )}
        {t === 'groups_2_of_3_playin' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BracketBlock title="Play-in" matches={b.playin || []} category={cat} phase="playin" onSaved={onSaved} testid="playin-block" />
            <BracketBlock title="Semifinais" matches={b.semi || []} category={cat} phase="semi" onSaved={onSaved} testid="semi-block" />
            <BracketBlock title="Final" matches={b.final} category={cat} phase="final" onSaved={onSaved} testid="final-block" />
            <BracketBlock title="3º Lugar" matches={b.third} category={cat} phase="third" onSaved={onSaved} testid="third-block" />
          </div>
        )}
        {t === 'groups_fem_4_3_semi' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fem-bracket">
            <div className="md:col-span-2 bg-blue-50 border border-[#0077B6] rounded-lg p-3 text-xs text-[#023E8A]">
              <strong>Como funciona:</strong> 1º de cada grupo vai direto à semifinal. Play-in: 2ºA x 3ºB e 2ºB x 3ºA — vencedores completam as semis. Semis: 1ºA x vencedor(2ºB x 3ºA) e 1ºB x vencedor(2ºA x 3ºB). Critério: vitórias; desempate por saldo (pontos feitos − sofridos).
            </div>
            <BracketBlock title="Play-in (2ºA x 3ºB · 2ºB x 3ºA)" matches={b.playin || []} category={cat} phase="playin" onSaved={onSaved} testid="playin-block" />
            <BracketBlock title="Semifinais" matches={b.semi || []} category={cat} phase="semi" onSaved={onSaved} testid="semi-block" />
            <BracketBlock title="Final" matches={b.final} category={cat} phase="final" onSaved={onSaved} testid="final-block" />
            <BracketBlock title="3º Lugar" matches={b.third} category={cat} phase="third" onSaved={onSaved} testid="third-block" />
          </div>
        )}
        {(t === 'groups_open_a8_b4' || t === 'groups_3grp_a8_b4') && (
          <div className="space-y-6" data-testid="open-bracket">
            <div className="bg-orange-50 border-2 border-[#E85D04] rounded-xl p-4">
              <h4 className="font-bold text-[#E85D04] mb-3">SÉRIE A — 2 primeiros de cada grupo (1º-8º)</h4>
              <div className="bg-white p-3 rounded border border-[#E6E2D8] mb-3">
                <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação geral (vitórias · desempate saldo) — chaveamento 1x8, 2x7, 3x6, 4x5</p>
                <ol className="text-xs text-[#1A1A1A] grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {(b.serie_a_seeds || []).map((r, i) => <li key={r.dupla}><span className="font-bold text-[#E85D04]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.v}v, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                </ol>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketBlock title="Quartas (1x8, 2x7, 3x6, 4x5)" matches={b.serie_a?.quarter || []} category={cat} phase="quarter_a" onSaved={onSaved} testid="serieA-qf" />
                <BracketBlock title="Semifinais" matches={b.serie_a?.semi || []} category={cat} phase="semi_a" onSaved={onSaved} testid="serieA-sm" />
                <BracketBlock title="Final" matches={b.serie_a?.final} category={cat} phase="final_a" onSaved={onSaved} testid="serieA-final" />
              </div>
            </div>
            <div className="bg-blue-50 border-2 border-[#0077B6] rounded-xl p-4">
              <h4 className="font-bold text-[#0077B6] mb-3">SÉRIE B — 3º de cada grupo (1º-4º)</h4>
              <div className="bg-white p-3 rounded border border-[#E6E2D8] mb-3">
                <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação (vitórias · desempate saldo) — chaveamento 1x4, 2x3</p>
                <ol className="text-xs text-[#1A1A1A] grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {(b.serie_b_seeds || []).map((r, i) => <li key={r.dupla}><span className="font-bold text-[#0077B6]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.v}v, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                </ol>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketBlock title="Semifinais (1x4, 2x3)" matches={b.serie_b?.semi || []} category={cat} phase="semi_b" onSaved={onSaved} testid="serieB-sm" />
                <BracketBlock title="Final" matches={b.serie_b?.final} category={cat} phase="final_b" onSaved={onSaved} testid="serieB-final" />
              </div>
            </div>
          </div>
        )}
        {t === 'groups_4_of_4_a_b' && (
          <div className="space-y-6">
            <div className="bg-orange-50 border-2 border-[#E85D04] rounded-xl p-4">
              <h4 className="font-bold text-[#E85D04] mb-3">SÉRIE A (1º-8º)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="bg-white p-3 rounded border border-[#E6E2D8]">
                  <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação geral (top 2 de cada grupo)</p>
                  <ol className="text-xs text-[#1A1A1A] space-y-0.5">
                    {(b.serie_a_seeds || []).map((r, i) => <li key={r.dupla}><span className="font-bold text-[#E85D04]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.pts}pts, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                  </ol>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketBlock title="Quartas" matches={b.serie_a?.quarter || []} category={cat} phase="quarter_a" onSaved={onSaved} testid="serieA-qf" />
                <BracketBlock title="Semifinais" matches={b.serie_a?.semi || []} category={cat} phase="semi_a" onSaved={onSaved} testid="serieA-sm" />
                <BracketBlock title="Final" matches={b.serie_a?.final} category={cat} phase="final_a" onSaved={onSaved} testid="serieA-final" />
                <BracketBlock title="3º Lugar" matches={b.serie_a?.third} category={cat} phase="third_a" onSaved={onSaved} testid="serieA-third" />
              </div>
            </div>
            <div className="bg-blue-50 border-2 border-[#0077B6] rounded-xl p-4">
              <h4 className="font-bold text-[#0077B6] mb-3">SÉRIE B (9º-16º)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="bg-white p-3 rounded border border-[#E6E2D8]">
                  <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação (3º e 4º de cada grupo)</p>
                  <ol className="text-xs text-[#1A1A1A] space-y-0.5">
                    {(b.serie_b_seeds || []).map((r, i) => <li key={r.dupla}><span className="font-bold text-[#0077B6]">{i + 9}º</span> {r.dupla} <span className="text-[#73706A]">({r.pts}pts, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                  </ol>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketBlock title="Quartas" matches={b.serie_b?.quarter || []} category={cat} phase="quarter_b" onSaved={onSaved} testid="serieB-qf" />
                <BracketBlock title="Semifinais" matches={b.serie_b?.semi || []} category={cat} phase="semi_b" onSaved={onSaved} testid="serieB-sm" />
                <BracketBlock title="Final" matches={b.serie_b?.final} category={cat} phase="final_b" onSaved={onSaved} testid="serieB-final" />
              </div>
            </div>
          </div>
        )}
        {t === 'groups_3_of_4_top8' && (
          <div className="space-y-3">
            <div className="bg-white p-3 rounded border border-[#E6E2D8]">
              <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação geral (top 8)</p>
              <ol className="text-xs text-[#1A1A1A] grid grid-cols-2 gap-x-4 gap-y-0.5">
                {(b.seeds || []).map((r, i) => <li key={r.dupla}><span className="font-bold text-[#E85D04]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.pts}pts)</span></li>)}
              </ol>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <BracketBlock title="Quartas" matches={b.series?.quarter || []} category={cat} phase="quarter" onSaved={onSaved} testid="misto-qf" />
              <BracketBlock title="Semifinais" matches={b.series?.semi || []} category={cat} phase="semi" onSaved={onSaved} testid="misto-sm" />
              <BracketBlock title="Final" matches={b.series?.final} category={cat} phase="final" onSaved={onSaved} testid="misto-final" />
              <BracketBlock title="3º Lugar" matches={b.series?.third} category={cat} phase="third" onSaved={onSaved} testid="misto-third" />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default function TournamentAdmin() {
  const [state, setState] = useState(null);
  const [tab, setTab] = useState('feminino');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tk-league/tournament`);
      setState(data);
    } catch { toast.error('Erro ao carregar'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const switchTab = (id) => { setTab(id); setEditing(false); };
  if (loading) return <Layout><div className="p-8 text-center text-[#73706A]">Carregando...</div></Layout>;
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="tournament-admin-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <Trophy size={28} className="text-[#E85D04]" weight="fill" />
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">TK League — Torneio</h1>
          </div>
          <Link to="/tk-league/placar" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-black" data-testid="live-score-link">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Placar ao Vivo
          </Link>
        </div>
        <div className="flex gap-2 mb-6 items-center flex-wrap" data-testid="tournament-tabs">
          {CATS.map(c => (
            <button key={c.id} onClick={() => switchTab(c.id)} className={`px-4 py-2 rounded-lg font-semibold text-sm ${tab === c.id ? 'bg-[#E85D04] text-white' : 'bg-white text-[#1A1A1A] border border-[#E6E2D8]'}`} data-testid={`tab-${c.id}`}>{c.label}</button>
          ))}
          <button onClick={() => setEditing(e => !e)} className={`ml-auto px-4 py-2 rounded-lg font-semibold text-sm border ${editing ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-[#1A1A1A] border-[#1A1A1A]'}`} data-testid="edit-duplas-toggle">{editing ? 'Fechar edição' : 'Editar Duplas'}</button>
        </div>
        {editing && state && (
          <GroupsEditor key={`ed-${tab}`} cat={tab} groups={state[tab].groups} onSaved={() => { setEditing(false); load(); }} onCancel={() => setEditing(false)} />
        )}
        {state && <CategoryPanel key={tab} cat={tab} data={state[tab]} onSaved={load} />}
      </div>
    </Layout>
  );
}
